import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Pin, Lock, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import CommentItem from "@/components/forums/CommentItem";
import CommentComposer from "@/components/forums/CommentComposer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMOJIS = ["🔥", "❤️", "💪", "🌈", "🥚"];
const TYPE_THEME = {
  question: { color: "#3B82F6", icon: "🤔" },
  win:      { color: "#D4AF37", icon: "💪" },
  vent:     { color: "#DC2626", icon: "😤" },
  recipe:   { color: "#10B981", icon: "🍳" },
};

export default function ForumPost() {
  const { post_id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/forums/posts/${post_id}`);
      setPost(data.post);
      setComments(data.comments || []);
    } catch {
      setPost(null);
    } finally {
      setLoading(false);
    }
  }, [post_id]);

  useEffect(() => { reload(); }, [reload]);

  const reactPost = async (emoji) => {
    if (!user) { window.dispatchEvent(new Event("omni:open-auth")); return; }
    try {
      const { data } = await axios.post(`${API}/forums/posts/${post_id}/react`, { emoji });
      const total = Object.values(data.reactions).reduce((a, b) => a + b, 0);
      setPost((p) => ({ ...p, reactions: data.reactions, total_reactions: total }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not react");
    }
  };

  const deletePost = async () => {
    if (!window.confirm("Delete this post and all its comments?")) return;
    try {
      await axios.delete(`${API}/forums/posts/${post_id}`);
      toast.success("Post deleted");
      navigate("/forums");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const togglePin = async () => {
    try {
      const { data } = await axios.post(`${API}/forums/posts/${post_id}/pin`, { pinned: !post.pinned });
      setPost((p) => ({ ...p, pinned: data.pinned }));
      toast.success(data.pinned ? "Pinned to top" : "Unpinned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pin");
    }
  };

  const toggleLock = async () => {
    try {
      const { data } = await axios.post(`${API}/forums/posts/${post_id}/lock`, { locked: !post.locked });
      setPost((p) => ({ ...p, locked: data.locked }));
      toast.success(data.locked ? "Comments locked" : "Comments unlocked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not lock");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="max-w-3xl mx-auto px-4 pt-28 pb-20 text-center text-neutral-500">Loading…</main>
        <Footer />
      </div>
    );
  }
  if (!post) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="max-w-3xl mx-auto px-4 pt-28 pb-20 text-center">
          <div className="text-6xl mb-3">🤷</div>
          <div className="font-display text-3xl mb-2">Post not found</div>
          <Link to="/forums" className="text-[#D4AF37] hover:underline">← Back to forums</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const theme = TYPE_THEME[post.post_type] || { color: "#71717A", icon: "💬" };
  const isOwner = user && user.role === "owner";
  const isOwnerOrAuthor = user && (isOwner || user.user_id === post.user_id);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="forum-post-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        <Link to="/forums" data-testid="forum-post-back" className="inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-[#D4AF37] mb-4">
          <ArrowLeft size={14} /> Back to forums
        </Link>

        <article
          className="bg-[#0F0F0F] border-2 border-zinc-900 p-6"
          style={{ borderLeftColor: theme.color, borderLeftWidth: "4px" }}
        >
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="text-2xl">{theme.icon}</span>
            <span
              className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-[1px]"
              style={{ backgroundColor: theme.color, color: "#0A0A0A" }}
            >
              {post.post_type}
            </span>
            {post.pinned && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                <Pin size={10}/> Pinned
              </span>
            )}
            {post.locked && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-neutral-500">
                <Lock size={10}/> Locked
              </span>
            )}
            <span className="text-xs text-neutral-500 ml-auto">{new Date(post.created_at).toLocaleString()}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mb-3 leading-tight" data-testid="forum-post-title">
            {post.title}
          </h1>
          <AuthorRow author={post.author} />
          {post.body && (
            <div className="mt-5 text-neutral-200 whitespace-pre-wrap leading-relaxed" data-testid="forum-post-body">
              {post.body}
            </div>
          )}
          <div className="flex items-center gap-2 mt-6 flex-wrap">
            {EMOJIS.map((e) => {
              const count = post.reactions?.[e] || 0;
              return (
                <button
                  key={e}
                  data-testid={`forum-post-react-${e}`}
                  onClick={() => reactPost(e)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 border-2 border-zinc-800 hover:border-[#D4AF37] transition-colors"
                >
                  <span>{e}</span>
                  {count > 0 && <span className="text-xs tabular-nums text-neutral-400">{count}</span>}
                </button>
              );
            })}
          </div>
          {isOwnerOrAuthor && (
            <div className="flex items-center gap-2 mt-6 pt-4 border-t border-zinc-900 flex-wrap">
              {isOwner && (
                <>
                  <button data-testid="forum-post-pin" onClick={togglePin} className="text-xs uppercase tracking-widest border border-[#D4AF37]/40 text-[#D4AF37] px-3 py-1.5 hover:bg-[#D4AF37] hover:text-black">
                    {post.pinned ? "Unpin" : "Pin"}
                  </button>
                  <button data-testid="forum-post-lock" onClick={toggleLock} className="text-xs uppercase tracking-widest border border-neutral-700 text-neutral-300 px-3 py-1.5 hover:bg-neutral-800">
                    {post.locked ? "Unlock" : "Lock"}
                  </button>
                </>
              )}
              <button data-testid="forum-post-delete" onClick={deletePost} className="text-xs uppercase tracking-widest inline-flex items-center gap-1 border border-[#DC2626]/40 text-[#DC2626] px-3 py-1.5 hover:bg-[#DC2626] hover:text-white">
                <Trash2 size={12}/> Delete
              </button>
            </div>
          )}
        </article>

        <section className="mt-8" data-testid="forum-comments">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare size={16} className="text-[#D4AF37]" />
            <h2 className="font-display text-2xl">{post.comment_count} {post.comment_count === 1 ? "comment" : "comments"}</h2>
          </div>
          {post.locked
            ? <div className="text-sm text-neutral-500 italic mb-4">🔒 Comments are locked on this post.</div>
            : <CommentComposer postId={post_id} onPosted={reload} />
          }
          <ul className="space-y-4 mt-5">
            {comments.map((c) => (
              <CommentItem key={c.comment_id} comment={c} postId={post_id} locked={post.locked} onChanged={reload} />
            ))}
          </ul>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function AuthorRow({ author }) {
  if (!author) return null;
  const inner = (
    <span className="inline-flex items-center gap-2 text-sm">
      {author.picture && <img src={author.picture} alt="" className="w-6 h-6 rounded-full object-cover" />}
      <span className="text-white font-bold">{author.name}</span>
      {author.handle && <span className="text-neutral-600">@{author.handle}</span>}
      {author.is_owner && <span className="text-[9px] uppercase tracking-widest text-[#D4AF37] font-bold">Owner</span>}
    </span>
  );
  if (author.public && author.handle) {
    return <Link to={`/u/${author.handle}`} className="hover:text-[#D4AF37]">{inner}</Link>;
  }
  return inner;
}
